import { useEffect, useState } from 'react';
import { councilWsClient } from '../services/councilWsClient';

// TH-1 — backstop window for a greeting that never narrates a terminal event.
export const GREETING_PENDING_TIMEOUT_MS = 15000;

/**
 * TH-1 (Resilience R1) — greetingPending suppresses BOTH trailhead empty-states
 * while the sherpa's first turn is in flight.
 *
 * Fail-safety is the whole point: the greeting is fire-and-forget and
 * `greeting_fired` commits BEFORE dispatch, so a failed greeting never retries.
 * We therefore clear on the first terminal event (turn_added OR peer_turn_failed)
 * AND a 15s timeout backstop — so a silent (no-event) pre-dispatch failure can
 * never leave both empty-states suppressed forever (a blank, never-clearing trail
 * is the canonical defect this guards). Reload self-heals via messages.length>0,
 * so no sessionStorage is needed.
 */
export function useGreetingPending(
  sessionId: string | undefined,
  initial: boolean,
): boolean {
  const [pending, setPending] = useState(!!initial);

  useEffect(() => {
    if (!sessionId || !pending) return;
    return councilWsClient.subscribe(sessionId, (frame: any) => {
      const et = frame?.event_type;
      if (et === 'turn_added' || et === 'peer_turn_failed') setPending(false);
    });
  }, [sessionId, pending]);

  useEffect(() => {
    if (!pending) return;
    const t = setTimeout(() => setPending(false), GREETING_PENDING_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [pending]);

  return pending;
}
